import { useEffect, useState, useRef } from "react";
import Navbar from "./Navbar";

/**
 * DHI HOSPITALITY - LOADING SCREEN
 * Section 6 from dhi_spec_v3.docx
 * Light background with dark terminal log window
 */

const FEED_LINES = [
  { type: "system", text: "Initializing audit protocol..." },
  { type: "data", text: "Connecting to hotel website..." },
  { type: "found", text: "Schema.org data detected" },
  { type: "data", text: "Extracting hotel metadata..." },
  { type: "found", text: "Hotel: {hotelName}" },
  { type: "data", text: "Location: {city}, {country}" },
  { type: "system", text: "Running parallel API checks..." },
  { type: "data", text: "Checking Google Hotels visibility..." },
  { type: "data", text: "Querying DataForSEO Hotels API..." },
  { type: "found", text: "Hotel found in Google Hotels" },
  { type: "data", text: "Fetching OTA price comparison..." },
  { type: "data", text: "Analyzing rate parity..." },
  { type: "found", text: "Direct rate identified" },
  { type: "data", text: "Calculating deviation..." },
  { type: "system", text: "Running Maps SERP lookup..." },
  { type: "data", text: "Fetching GMB Profile..." },
  { type: "found", text: "GMB profile located" },
  { type: "data", text: "Analyzing GMB completeness..." },
  { type: "data", text: "Running PageSpeed API..." },
  { type: "found", text: "Performance metrics captured" },
  { type: "data", text: "Analyzing booking widget..." },
  { type: "data", text: "Checking trust signals..." },
  { type: "system", text: "Processing AI analysis..." },
  { type: "data", text: "Generating projections..." },
  { type: "system", text: "Compiling audit report..." },
  { type: "system", text: "Preparing dashboard..." },
];

export const ProgressTracker = ({ isComplete = false, extractedData = null }) => {
  const [progress, setProgress] = useState(0);
  const [feedLines, setFeedLines] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const feedRef = useRef(null);
  const lineIndexRef = useRef(0);

  const displayData = {
    hotelName: extractedData?.hotelName || extractedData?.name || "Your Hotel",
    city: extractedData?.city || null,
    country: extractedData?.country || null,
  };

  const replacePlaceholders = (text) => {
    let result = text.replace("{hotelName}", displayData.hotelName);
    
    if (text.includes("{city}") || text.includes("{country}")) {
      if (displayData.city && displayData.country) {
        result = `Location: ${displayData.city}, ${displayData.country}`;
      } else if (displayData.city || displayData.country) {
        result = `Location: ${displayData.city || displayData.country}`;
      } else {
        result = "Location: Extracting from website...";
      }
    }
    
    return result;
  };

  useEffect(() => {
    if (isComplete) {
      setProgress(100);
      return;
    }
    const progressInterval = setInterval(() => {
      setProgress(prev => prev >= 95 ? 95 : prev + 0.5);
    }, 200);
    return () => clearInterval(progressInterval);
  }, [isComplete]);

  // Elapsed time tracker for dynamic status text
  useEffect(() => {
    if (isComplete) return;
    const timer = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [isComplete]);

  const statusText = elapsed < 45
    ? "This typically takes 45–60 seconds"
    : elapsed < 90
    ? "Taking a bit longer than usual"
    : "Almost there...";

  useEffect(() => {
    if (isComplete) return;
    const addLine = () => {
      if (lineIndexRef.current >= FEED_LINES.length) {
        lineIndexRef.current = Math.floor(FEED_LINES.length / 2);
      }
      const line = FEED_LINES[lineIndexRef.current];
      const processedText = replacePlaceholders(line.text);
      setFeedLines(prev => {
        const newLines = [...prev, { ...line, text: processedText, id: Date.now() }];
        return newLines.slice(-10);
      });
      lineIndexRef.current++;
    };
    addLine();
    const feedInterval = setInterval(addLine, 1200 + Math.random() * 800);
    return () => clearInterval(feedInterval);
  }, [isComplete]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [feedLines]);

  return (
    <div 
      className="fixed inset-0 flex flex-col z-50"
      style={{ background: '#F8F9FF' }}
      data-testid="progress-tracker"
    >
      {/* Fixed Navbar - same as all other screens */}
      <Navbar />
      
      {/* Main Content - Vertically centred */}
      <div 
        className="flex-1 flex items-center justify-center px-4 sm:px-6"
        style={{ paddingTop: '64px' }}
      >
        <div className="w-full max-w-[480px] text-center">
          {/* Eyebrow label */}
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'rgba(10,10,26,0.35)',
            marginBottom: '12px'
          }}>
            ANALYZING YOUR PROPERTY
          </p>
          
          {/* Hotel name */}
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '36px',
            fontWeight: 800,
            color: '#0A0A1A',
            letterSpacing: '-0.02em',
            marginBottom: '8px'
          }}>
            {displayData.hotelName}
          </h2>
          
          {/* Sub-label */}
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            color: 'rgba(10,10,26,0.45)',
            marginBottom: '48px'
          }}>
            Running analysis across 10 parameters
          </p>

          {/* Progress bar */}
          <div style={{ marginBottom: '32px' }}>
            {/* Label row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '12px',
              color: 'rgba(10,10,26,0.4)',
              marginBottom: '10px'
            }}>
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            {/* Track */}
            <div style={{
              height: '6px',
              background: 'rgba(10,10,26,0.08)',
              borderRadius: '99px',
              overflow: 'hidden'
            }}>
              {/* Fill */}
              <div 
                style={{ 
                  height: '100%',
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #2D4DE0, #4A6FE3)',
                  borderRadius: '99px',
                  transition: 'width 0.3s ease-out'
                }}
                data-testid="progress-bar"
              />
            </div>
          </div>

          {/* Log window - dark terminal */}
          <div 
            ref={feedRef}
            style={{
              background: '#0A0A1A',
              borderRadius: '12px',
              padding: '20px 24px',
              maxHeight: '200px',
              overflowY: 'auto',
              textAlign: 'left'
            }}
            data-testid="intelligence-feed"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {feedLines.map((line, index) => (
                <div 
                  key={line.id || index}
                  style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    fontFamily: "'Courier New', monospace",
                    fontSize: '12px',
                    lineHeight: '1.8',
                    animation: 'fadeIn 0.3s ease-out forwards' 
                  }}
                >
                  <span style={{ 
                    flexShrink: 0,
                    color: line.type === "found" ? '#1A7A52' : 
                           line.type === "system" ? '#2D4DE0' : 
                           'rgba(255,255,255,0.35)'
                  }}>
                    {line.type === "system" ? "[SYS]" : line.type === "found" ? "[OK]" : "[...]"}
                  </span>
                  <span style={{ 
                    color: line.type === "found" ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)'
                  }}>
                    {line.text}
                  </span>
                </div>
              ))}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontFamily: "'Courier New', monospace",
                fontSize: '12px'
              }}>
                <span style={{ color: 'rgba(255,255,255,0.35)' }}>[...]</span>
                <span className="animate-pulse" style={{ color: 'rgba(255,255,255,0.45)' }}>_</span>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '12px',
            color: 'rgba(10,10,26,0.3)',
            marginTop: '24px'
          }}>
            {statusText}
          </p>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(4px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
      `}</style>
    </div>
  );
};

export default ProgressTracker;
